import axios from "axios";
import * as React from "react";
import * as ReactQuery from "react-query";

import {
  CustomerInformationQuery__QueryKey,
  CustomerInformation_Address_Response,
  GraphqlWrappedData,
  InitialStateMinimumRequirements_GraphQl,
  useReactQueryClient,
} from "../../../Common";

import { DataMutation_DeleteCustomerAddress_QueryKey } from "./data-mutation--delete-customer-address--query-key-factory";

export interface DataMutationDeleteCustomerAddressConsumerInterface {
  data: DataMutationDeleteCustomerAddress_Result["data"];
  status: DataMutationDeleteCustomerAddress_Result["status"];
}
const defaultValue =
  {} as unknown as DataMutationDeleteCustomerAddressConsumerInterface;
const DataMutationDeleteCustomerAddressContext =
  React.createContext(defaultValue);

export interface DataMutationDeleteCustomerAddress_ControllerInterface {
  mutate: DataMutationDeleteCustomerAddress_Result["mutate"];
  mutateAsync: DataMutationDeleteCustomerAddress_Result["mutateAsync"];
}
const controllerDefaultValue =
  {} as unknown as DataMutationDeleteCustomerAddress_ControllerInterface;
const DataMutationDeleteCustomerAddress_ControllerContext = React.createContext(
  controllerDefaultValue
);

export interface DataMutationDeleteCustomerAddress_Request {
  addressId: CustomerInformation_Address_Response["id"];
}
export type DataMutationDeleteCustomerAddress_Result =
  ReactQuery.UseMutationResult<
    boolean,
    unknown,
    DataMutationDeleteCustomerAddress_Request,
    unknown
  >;
export type GraphqlResponse_DeleteCustomerAddress = GraphqlWrappedData<
  "deleteCustomerAddress",
  boolean
>;

export interface DataMutationDeleteCustomerAddressProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_GraphQl;
  };
}
export const DataMutationDeleteCustomerAddressProvider: React.FunctionComponent<
  DataMutationDeleteCustomerAddressProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const queryClient = useReactQueryClient();

  const mutationKey: ReactQuery.MutationKey = React.useMemo(() => {
    return DataMutation_DeleteCustomerAddress_QueryKey.deleteCustomerAddress();
  }, []);

  const mutationFn: ReactQuery.MutationFunction<
    boolean,
    DataMutationDeleteCustomerAddress_Request
  > = React.useCallback(
    async (request) => {
      const gqlQuery = `
        mutation {
          deleteCustomerAddress(id: ${request.addressId})
        }
      `;

      const headers = {
        "Content-Type": "application/json",
        Store: initialState.store_code,
      };

      const { data } = await axios.post<GraphqlResponse_DeleteCustomerAddress>(
        "/graphql",
        { query: gqlQuery },
        { headers }
      );

      let wasThisSuccessful = false;
      if (data && data.data) {
        const thisDeleteWasASuccess = !!data.data.deleteCustomerAddress;
        if (thisDeleteWasASuccess) {
          wasThisSuccessful = true;
        }
      }

      if (!wasThisSuccessful) {
        throw new Error("Could not delete address");
      }

      return wasThisSuccessful;
    },
    [initialState]
  );

  const handleSuccess = React.useCallback(() => {
    queryClient.invalidateQueries(
      CustomerInformationQuery__QueryKey.customerInformation()
    );
  }, [queryClient]);

  const mutation = ReactQuery.useMutation(mutationKey, mutationFn, {
    onSuccess: handleSuccess,
  });

  const value: DataMutationDeleteCustomerAddressConsumerInterface =
    React.useMemo(
      () => ({
        data: mutation.data,
        status: mutation.status,
      }),
      [mutation.data, mutation.status]
    );
  const controllerValue: DataMutationDeleteCustomerAddress_ControllerInterface =
    React.useMemo(
      () => ({
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
      }),
      [mutation, mutation.mutateAsync]
    );
  return (
    <DataMutationDeleteCustomerAddressContext.Provider value={value}>
      <DataMutationDeleteCustomerAddress_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </DataMutationDeleteCustomerAddress_ControllerContext.Provider>
    </DataMutationDeleteCustomerAddressContext.Provider>
  );
};

export const useDataMutationDeleteCustomerAddress = () => {
  const context = React.useContext(DataMutationDeleteCustomerAddressContext);
  if (context === defaultValue) {
    throw new Error(
      "useDataMutationDeleteCustomerAddress must be used within a DataMutationDeleteCustomerAddressProvider"
    );
  }
  return context;
};

export const useDataMutationDeleteCustomerAddress_asController = () => {
  const context = React.useContext(
    DataMutationDeleteCustomerAddress_ControllerContext
  );
  if (context === controllerDefaultValue) {
    throw new Error(
      "useDataMutationDeleteCustomerAddress_asController must be used within a DataMutationDeleteCustomerAddressProvider"
    );
  }
  return context;
};
